import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, defaultIfEmpty, filter, map, take } from 'rxjs/operators';
import { AppStateService, WorkspaceBootstrapState } from '../services';

type WorkspaceAccessResult = true | UrlTree;

export const workspaceAccessGuard: CanActivateFn = (_route, routerState) => {
  const appState = inject(AppStateService);
  const router = inject(Router);
  const rejectedAccess = router.createUrlTree(['/login'], {
    queryParams: { returnUrl: routerState.url },
  });
  const resolveAccess = (
    state: WorkspaceBootstrapState,
  ): WorkspaceAccessResult =>
    hasAuthenticatedWorkspaceContext(state) ? true : rejectedAccess;

  try {
    const initialState = appState.getStateSnapshot();
    if (isTerminalWorkspaceContext(initialState)) {
      return resolveAccess(initialState);
    }

    if (!initialState.isLoading) {
      appState.bootstrapWorkspaceContext();
    }

    return appState.state$.pipe(
      filter(isTerminalWorkspaceContext),
      take(1),
      map(resolveAccess),
      defaultIfEmpty(rejectedAccess),
      catchError((): Observable<WorkspaceAccessResult> => of(rejectedAccess)),
    );
  } catch {
    return rejectedAccess;
  }
};

export function isTerminalWorkspaceContext(
  state: WorkspaceBootstrapState,
): boolean {
  return !state.isLoading && (state.isLoaded || state.errorMessage.length > 0);
}

export function hasAuthenticatedWorkspaceContext(
  state: WorkspaceBootstrapState,
): boolean {
  return (
    isTerminalWorkspaceContext(state) &&
    state.isLoaded &&
    state.errorMessage.length === 0 &&
    state.user?.isAuthenticated === true
  );
}
